import { appRoutes, RouterEntitiesId } from '@clearverify/constants';
import type { Nullable } from '@clearverify/types';

import { CompanyType, PeoCompanyType, PeoPayrollProviderCompanyType } from 'src/api';

import type { EmployeeInviteLinkArgs } from './useCopyEmployeeInviteLink';

type PeoPayrollProviderScope = {
  id: string;
  companyType: PeoPayrollProviderCompanyType;
};

type PeoScope = {
  id: string;
  peoPayrollProvider?: Nullable<PeoPayrollProviderScope>;
};

type EmployerScope = {
  id: string;
  controlledBy: CompanyType;
  peo?: Nullable<PeoScope & { companyType: PeoCompanyType }>;
  peoPayrollProvider?: Nullable<PeoPayrollProviderScope>;
};

/**
 * Employees sign up via the topmost non-Partnership entity in the control
 * chain (Employer → PEO → PEO Payroll Provider); a Partnership parent keeps
 * the link scoped to the entity below it.
 * Mirrored in worker/src/tasks/common/sendNotificationsCreateVerificationRequest/utils/getInviteLink.ts.
 */
export const getPeoInviteLinkArgs = (peo: PeoScope): EmployeeInviteLinkArgs => {
  const parent = peo.peoPayrollProvider;
  if (parent && parent.companyType !== PeoPayrollProviderCompanyType.Partnership) {
    return {
      route: appRoutes.auth.signupEmployeePeoPayrollProvider,
      paramKey: RouterEntitiesId.peoPayrollProvider,
      entityId: parent.id,
    };
  }
  return {
    route: appRoutes.auth.signupEmployeePeo,
    paramKey: RouterEntitiesId.peo,
    entityId: peo.id,
  };
};

export const getEmployerInviteLinkArgs = (
  employer: Nullable<EmployerScope>,
): Nullable<EmployeeInviteLinkArgs> => {
  if (!employer) return null;

  if (
    employer.controlledBy === CompanyType.Peo &&
    employer.peo &&
    employer.peo.companyType !== PeoCompanyType.Partnership
  ) {
    return {
      route: appRoutes.auth.signupEmployeePeo,
      paramKey: RouterEntitiesId.peo,
      entityId: employer.peo.id,
    };
  }

  if (
    employer.controlledBy === CompanyType.PeoPayrollProvider &&
    employer.peoPayrollProvider &&
    employer.peoPayrollProvider.companyType !== PeoPayrollProviderCompanyType.Partnership
  ) {
    return {
      route: appRoutes.auth.signupEmployeePeoPayrollProvider,
      paramKey: RouterEntitiesId.peoPayrollProvider,
      entityId: employer.peoPayrollProvider.id,
    };
  }

  return {
    route: appRoutes.auth.signupEmployee,
    paramKey: RouterEntitiesId.employer,
    entityId: employer.id,
  };
};
